// Pure, framework-free core for E1 (Suggest-to-Nobilis): status vocabulary, colors, allowed
// staff status-transitions and convert eligibility. No React/service imports so it is trivially
// unit-testable; the staff queue + detail modal wire it up.

export type SuggestionStatus = 'received' | 'under_review' | 'converted' | 'declined' | 'archived'

export interface SuggestionStatusMeta {
  value: SuggestionStatus
  label: string
  color: string
}

// Order = the workflow order shown in the status filter. Colors on the Nobilis palette.
export const SUGGESTION_STATUSES: SuggestionStatusMeta[] = [
  {value: 'received', label: 'Received', color: '#978B63'}, // gold
  {value: 'under_review', label: 'Under Review', color: '#163944'}, // teal
  {value: 'converted', label: 'Converted', color: '#3F7D5A'}, // green
  {value: 'declined', label: 'Declined', color: '#540008'}, // maroon
  {value: 'archived', label: 'Archived', color: '#9AA0A6'}, // grey
]

const BY_VALUE: Record<string, SuggestionStatusMeta> = SUGGESTION_STATUSES.reduce(
  (acc, s) => ({...acc, [s.value]: s}),
  {}
)

export const statusLabel = (status?: string | null): string =>
  (status && BY_VALUE[status]?.label) || 'Received'

export const statusColor = (status?: string | null): string =>
  (status && BY_VALUE[status]?.color) || '#978B63'

// Staff status-transitions (excludes "converted", which is reached via the convert action, not a
// plain status set). Declined/archived can be reopened to under_review; converted is terminal.
export const ALLOWED_TRANSITIONS: Record<SuggestionStatus, SuggestionStatus[]> = {
  received: ['under_review', 'declined', 'archived'],
  under_review: ['declined', 'archived'],
  declined: ['under_review', 'archived'],
  archived: ['under_review'],
  converted: [],
}

export const nextStatuses = (status?: string | null): SuggestionStatus[] =>
  (status && ALLOWED_TRANSITIONS[status as SuggestionStatus]) || []

export const canTransition = (from: string | null | undefined, to: SuggestionStatus): boolean =>
  nextStatuses(from).includes(to)

// Convert-to-experience is offered while the idea is still in the intake pipeline.
export const canConvert = (status?: string | null): boolean =>
  status === 'received' || status === 'under_review'
